import {
  useCurrencySelect,
  useEthers,
  useNumberInput,
  usePair,
  useLiquidityApproval,
  useLiquidityBalance,
} from '../../hooks'
import { BigNumber } from '@ethersproject/bignumber'
import { formatUnits } from '@ethersproject/units'
import { CurrencyValue, formatCurrency, getPairCurrencies } from '../../model'
import { bigNumberFromString, BN_10_18, normalizeTo18Decimals } from '../../utils'
import { useEffect, useState } from 'react'
import { useApiTokenPrices } from '../../providers'

export enum LiquidityState {
  LOADING = 'LOADING',
  INSUFFICIENT_LIQUIDITY = 'INSUFFICIENT_LIQUIDITY',
  INSUFFICIENT_LIQUIDITY_IN_PAIR = 'INSUFFICIENT_LIQUIDITY_IN_PAIR',
  NO_ISSUE = 'NO_ISSUE',
  MISSING_INPUT = 'MISSING_INPUT',
  EMPTY = 'EMPTY',
  NEED_APPROVAL = 'NEED_APPROVAL',
  PAIR_DOES_NOT_EXIST = 'PAIR_DOES_NOT_EXIST',
}

const STATES_TO_SHOW_OUTPUT = [
  LiquidityState.MISSING_INPUT,
  LiquidityState.NEED_APPROVAL,
  LiquidityState.NO_ISSUE,
  LiquidityState.INSUFFICIENT_LIQUIDITY,
]

export function useRemoveLiquidity({ pairAddress }: { pairAddress: string }) {
  const [liquidityValue, setLiquidityValue] = useState(BigNumber.from(0))
  const [liquidityInputValue, setLiquidityInputValue] = useNumberInput(18)

  const { chainId } = useEthers()
  const currencies = useCurrencySelect()
  const { pair, exists } = usePair(currencies.first, currencies.second)
  const liquidityApproval = useLiquidityApproval(pair)
  const liquidityBalance = useLiquidityBalance(pair)

  const [firstCurrencyValue, setFirstCurrencyValue] = useState(new CurrencyValue(currencies.first, BigNumber.from(0)))
  const [secondCurrencyValue, setSecondCurrencyValue] = useState(
    new CurrencyValue(currencies.second, BigNumber.from(0))
  )

  useEffect(() => {
    if (!chainId) {
      return
    }
    const tokens = getPairCurrencies(pairAddress, chainId)
    if (tokens) {
      currencies.setCurrencies(tokens[0], tokens[1])
    }
  }, [chainId])

  useEffect(() => {
    if (!pair) {
      return
    }
    const burnOutput = pair.getBurnOutput(liquidityValue)
    setFirstCurrencyValue(burnOutput.first)
    setSecondCurrencyValue(burnOutput.second)
  }, [liquidityValue, pair?.address, currencies.first, currencies.second])

  useEffect(() => {
    setLiquidityValue(bigNumberFromString(18, liquidityInputValue))
  }, [liquidityInputValue])

  function setLiquidityInputMax() {
    if (liquidityBalance) {
      setLiquidityInputValue(formatUnits(liquidityBalance, 18))
    }
  }

  const apiTokenPrices = useApiTokenPrices()
  const firstPriceSymbol = currencies.first.isNative() ? 'WETH' : currencies.first.symbol
  const secondPriceSymbol = currencies.second.isNative() ? 'WETH' : currencies.second.symbol
  let firstUsdValue
  let secondUsdValue
  let firstUsdValue18
  let secondUsdValue18
  if (apiTokenPrices && apiTokenPrices[firstPriceSymbol] && apiTokenPrices[secondPriceSymbol]) {
    const firstUsdValue18 = normalizeTo18Decimals(firstCurrencyValue.value, firstCurrencyValue.currency.decimals)
      .mul(apiTokenPrices[firstPriceSymbol].price)
      .div(BN_10_18)
    const secondUsdValue18 = normalizeTo18Decimals(secondCurrencyValue.value, secondCurrencyValue.currency.decimals)
      .mul(apiTokenPrices[secondPriceSymbol].price)
      .div(BN_10_18)
    firstUsdValue = `~$${formatCurrency(18, firstUsdValue18, 2)}`
    secondUsdValue = `~$${formatCurrency(18, secondUsdValue18, 2)}`
  }

  let state = LiquidityState.NO_ISSUE
  if (!exists) {
    state = LiquidityState.PAIR_DOES_NOT_EXIST
  } else if (!pair || !currencies.first || !currencies.second) {
    state = LiquidityState.LOADING
  } else if (liquidityBalance.isZero()) {
    state = LiquidityState.EMPTY
  } else if (!liquidityInputValue) {
    state = LiquidityState.MISSING_INPUT
  } else if (liquidityValue.gt(pair.getTotalSupply())) {
    state = LiquidityState.INSUFFICIENT_LIQUIDITY_IN_PAIR
  } else if (liquidityValue.isZero() || liquidityBalance.lt(liquidityValue)) {
    state = LiquidityState.INSUFFICIENT_LIQUIDITY
  } else if (!liquidityApproval || liquidityApproval.lt(liquidityValue)) {
    state = LiquidityState.NEED_APPROVAL
  }

  return {
    liquidityValue,
    liquidityInputValue,
    liquidityBalance,
    pair,
    firstCurrencyValue,
    firstUsdValue,
    firstUsdValue18,
    secondCurrencyValue,
    secondUsdValue,
    secondUsdValue18,
    state,
    showOutput: STATES_TO_SHOW_OUTPUT.includes(state),
    currencies,
    setLiquidityInputValue,
    setLiquidityInputMax,
  }
}
